import React, { useCallback, useEffect, useState } from 'react';

import AppHeader from "@/components/header/AppHeader";
import { Typography, Anchor, Tag, Tooltip, message, theme, Collapse } from 'antd';
import {
    CopyOutlined, ArrowUpOutlined,
    BookOutlined, ApiOutlined, CodeOutlined, WarningOutlined,
    InfoCircleOutlined, DownOutlined
} from '@ant-design/icons';

import { ENDPOINT_GROUPS, type EndpointDoc } from './docsData';

import './styles/docs.scss';

const { Title, Paragraph, Text } = Typography;

/* ── helpers ──────────────────────────────────────────────── */

const METHOD_CLR: Record<string, string> = {
    GET:    '#52c41a',
    POST:   '#1677ff',
    PUT:    '#fa8c16',
    DELETE: '#ff4d4f',
    PATCH:  '#722ed1',
};

const copyText = async (text: string, key: string) => {
    try {
        await navigator.clipboard.writeText(text);
        message.success({ content: `Скопировано: ${key}`, key });
    } catch {
        // fallback for non-https / old browsers
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        message.success({ content: `Скопировано: ${key}`, key });
    }
};

/* ── data ──────────────────────────────────────────────────── */

type CodeSample = { lang: string; label: string; code: string; };

const MOVIE_CREATE_EXAMPLES: CodeSample[] = [    {
        lang: 'curl', label: 'cURL',
        code: `curl -X POST "\${API_BASE_URL}/movies" \\
  -H "Authorization: Bearer \${ACCESS_TOKEN}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Интерстеллар",
    "releaseYear": 2014,
    "imdbId": "tt0816692",
    "voiceovers": [
      { "voiceoverId": 1, "videoFile": "movies/interstellar.mp4" }
    ]
  }'`
    },
    {
        lang: 'php', label: 'PHP (Guzzle)',
        code: `<?php

use GuzzleHttp\\Client;

$client = new Client([
    'base_uri' => getenv('API_BASE_URL'),
    'headers'  => ['Authorization' => 'Bearer ' . getenv('ACCESS_TOKEN')],
]);

$response = $client->get('/movies', [
    'query' => ['page' => 1, 'per_page' => 50],
]);

$data = json_decode($response->getBody()->getContents(), true);
foreach (($data['data'] ?? []) as $movie) {
    echo $movie['name'] . ' — ' . $movie['statusName'] . PHP_EOL;
}`
    },
    {
        lang: 'python', label: 'Python',
        code: `import os, requests

base_url = os.environ["API_BASE_URL"]
token   = os.environ["ACCESS_TOKEN"]

response = requests.get(
    f"{base_url}/videos/128/status",
    headers={"Authorization": f"Bearer {token}"},
    timeout=30,
)
response.raise_for_status()
data = response.json()
print(f"Статус: {data['data']['statusName']} ({data['data']['status']})")`
    },
    {
        lang: 'javascript', label: 'JavaScript',
        code: `const BASE_URL = process.env.API_BASE_URL;
const TOKEN    = process.env.ACCESS_TOKEN;

async function addVoiceover(movieId) {
  const res = await fetch(
    \`\${BASE_URL}/movies/\${movieId}/voiceovers\`,
    {
      method:  'POST',
      headers: {
        Authorization: \`Bearer \${TOKEN}\`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        voiceoverId: 2,
        videoFile:   'movies/interstellar_eng.mp4',
      }),
    }
  );
  if (!res.ok) throw new Error((await res.json()).message);
  return res.json();
}`
    },
];

const MOVIE_RESPONSE = `{
  "success": true,
  "status": 201,
  "data": {
    "id": 128,
    "name": "Интерстеллар",
    "nameOriginal": "Interstellar",
    "releaseYear": 2014,
    "kinopoiskId": 462682,
    "imdbId": "tt0816692",
    "status": "processing",
    "statusId": 3,
    "statusName": "Конвертация",
    "voiceovers": [{
      "id": 552, "videoId": 128, "name": "Дублированный",
      "hasInternalAds": false, "active": true, "sortOrder": 0,
      "exclusiveUntil": null,
      "videoFile": { "name": "128.tmp", "size": 2147483648, "status": "processing" }
    }]
  }
}`;

/* ── Auth examples ─────────────────────────────────────── */

const LOGIN_REQUEST = `POST /api/v1/login-cm HTTP/1.1
Host: api.vibix.org
Content-Type: application/json

{
  "email": "admin@vibix.biz",
  "password": "ваш-пароль"
}`;

const LOGIN_RESPONSE = `HTTP/1.1 200 OK
Content-Type: application/json

{
  "access_token": "1|u3Xa9fNqZ8bKpW2cLmR5sTvY7dJhE4gB",
  "token_type": "Bearer",
  "role": "superadmin",
  "login": "admin",
  "id": 1
}`;

const LOGIN_EXAMPLES: CodeSample[] = [
    {
        lang: 'curl', label: 'cURL',
        code: `curl -X POST "https://api.vibix.org/api/v1/login-cm" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "admin@vibix.biz",
    "password": "ваш-пароль"
  }'

# Ответ: access_token — это и есть ваш Bearer-токен
# Сохраните его и передавайте в заголовке Authorization:`,
    },
    {
        lang: 'php', label: 'PHP (Guzzle)',
        code: `<?php

use GuzzleHttp\\Client;

$client = new Client(['base_uri' => 'https://api.vibix.org']);

$response = $client->post('/api/v1/login-cm', [
    'json' => [
        'email'    => 'admin@vibix.biz',
        'password' => 'ваш-пароль',
    ],
]);

$data    = json_decode($response->getBody()->getContents(), true);
$token   = $data['access_token'];
$headers = ['Authorization' => "Bearer {$token}"];

echo "Токен: {$token}\\n";`,
    },
    {
        lang: 'python', label: 'Python',
        code: `import requests

response = requests.post(
    "https://api.vibix.org/api/v1/login-cm",
    json={"email": "admin@vibix.biz", "password": "ваш-пароль"},
    timeout=30,
)
response.raise_for_status()

token = response.json()["access_token"]
headers = {"Authorization": f"Bearer {token}"}
print(f"Токен: {token}")`,
    },
    {
        lang: 'javascript', label: 'JavaScript',
        code: `const response = await fetch(
  'https://api.vibix.org/api/v1/login-cm',
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: 'admin@vibix.biz',
      password: 'ваш-пароль',
    }),
  }
);
if (!response.ok) throw new Error((await response.json()).message);

const { access_token: token } = await response.json();
const headers = { Authorization: \`Bearer \${token}\` };
console.log('Токен:', token);`,
    },
];

const AUTH_HEADER_EXAMPLE = `// Все последующие запросы к Upload API

curl "https://api.vibix.org/api/v1/upload/movies" \\
  -H "Authorization: Bearer 1|u3Xa9fNqZ8bKpW2cLmR5sTvY7dJhE4gB" \\
  -H "Accept: application/json"`;

const MASS_EPISODES_EXAMPLE = `{
  "filmName": "Эпизод {number}",
  "voiceoverId": 1,
  "files": [
    { "videoFile": "serial/s01e01.mp4", "sortOrder": 1 },
    { "videoFile": "serial/s01e02.mp4", "sortOrder": 2 },
    { "videoFile": "serial/s01e03.mp4", "sortOrder": 3 }
  ]
}`;

const ERROR_BODY = `{
  "success": false,
  "message": "Описание ошибки",
  "status": 422,
  "errors": { "title": ["Некорректное значение поля: Title."] }
}`;

const TOC_ITEMS = [
    { key: 'auth',        href: '#auth',        title: 'Аутентификация' },
    { key: 'conventions', href: '#conventions',  title: 'Общие соглашения' },
    { key: 'endpoints',   href: '#endpoints',    title: 'Эндпоинты' },
    { key: 'movies',      href: '#movies',       title: 'Фильмы' },
    { key: 'movies-examples', href: '#movies-examples', title: '· Примеры кода' },
    { key: 'serials',     href: '#serials',      title: 'Сериалы и эпизоды' },
    { key: 'voiceovers',  href: '#voiceovers',   title: 'Озвучки' },
    { key: 'reference',   href: '#reference',    title: 'Справочники' },
    { key: 'errors',      href: '#errors',       title: 'Коды ошибок' },
    { key: 'examples',    href: '#examples',     title: 'Примеры кода' },
    { key: 'tips',        href: '#tips',         title: 'Рекомендации' },
];

/* ── sub-components ───────────────────────────────────────── */

const Section: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
    <section id={id} className="ds-section">{children}</section>
);

const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Title level={2} className="ds-h2">{children}</Title>
);

const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Title level={3} className="ds-h3">{children}</Title>
);

const H4: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Title level={4} className="ds-h4">{children}</Title>
);

const Callout: React.FC<{ type?: 'note'|'warning'; title?: string; children: React.ReactNode }> =
    ({ type = 'note', title, children }) => (
    <div className={`ds-callout ds-callout--${type}`}>
        <span className="ds-callout__icon">
            {type === 'warning' ? <WarningOutlined /> : <InfoCircleOutlined />}
        </span>
        <div className="ds-callout__body">
            {title && <strong className="ds-callout__title">{title}</strong>}
            <div>{children}</div>
        </div>
    </div>
);

const CodeBlock: React.FC<{ code: string; label?: string }> = ({ code, label }) => (
    <div className="ds-code-wrap">
        {label && <div className="ds-code__lang">{label}</div>}
        <Tooltip title="Скопировать" placement="left">
            <button className="ds-code__copy" onClick={() => copyText(code, label || 'код')}>
                <CopyOutlined />
            </button>
        </Tooltip>
        <pre className="ds-code"><code>{code}</code></pre>
    </div>
);

const MethodBadge: React.FC<{ method: string }> = ({ method }) => (
    <Tag color={METHOD_CLR[method] || '#888'} className="ds-method-badge">{method}</Tag>
);

const ParamTable: React.FC<{ rows: string[][] }> = ({ rows }) => (
    <div className="ds-table-wrap">
        <table className="ds-table">
            <thead><tr><th>Поле</th><th>Тип</th><th>Обяз.</th><th>Описание</th></tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i}>
                    <td><code>{r[0]}</code></td>
                    <td><Text type="secondary">{r[1]}</Text></td>
                    <td>{r[2] === '✅' ? <Text type="danger">✅</Text> : <Text type="secondary">—</Text>}</td>
                    <td>{r[3]}</td>
                </tr>
            ))}</tbody>
        </table>
    </div>
);

const StatusTable: React.FC<{ rows: string[][] }> = ({ rows }) => (
    <div className="ds-table-wrap">
        <table className="ds-table">
            <thead><tr><th>Код</th><th>Описание</th><th>Сообщение</th></tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} className={r[0].startsWith('2') ? 'ds-tr--ok' : r[0].startsWith('4') ? 'ds-tr--warn' : ''}>
                    <td><Tag color={r[0].startsWith('2') ? 'green' : r[0].startsWith('4') ? 'orange' : 'red'}>{r[0]}</Tag></td>
                    <td>{r[1]}</td>
                    <td><Text type="secondary" italic>{r[2]}</Text></td>
                </tr>
            ))}</tbody>
        </table>
    </div>
);

const CodeTabs: React.FC<{ samples: CodeSample[] }> = ({ samples }) => {
    const { token: { colorPrimary } } = theme.useToken();
    const [active, setActive] = useState(samples[0]?.lang || '');
    const current = samples.find(s => s.lang === active) || samples[0];
    return (
        <div className="ds-code-tabs">
            <nav className="ds-code-tabs__nav">
                {samples.map(s => (
                    <button key={s.lang} className={`ds-code-tabs__tab${active===s.lang?' ds-code-tabs__tab--active':''}`}
                            style={active===s.lang ? {borderBottomColor: colorPrimary} : undefined}
                            onClick={() => setActive(s.lang)}>
                        {s.label}
                    </button>
                ))}
            </nav>
            <CodeBlock code={current.code} label={current.label} />
        </div>
    );
};

/* ── Endpoint accordion ────────────────────────────────────── */

const EndpointPanel: React.FC<{ ep: EndpointDoc }> = ({ ep }) => (
    <div className="ds-endpoint">
        <div className="ds-endpoint__blocks">
            <div className="ds-endpoint__block">
                <div className="ds-endpoint__label">Запрос</div>
                <CodeBlock code={ep.request} />
            </div>
            <div className="ds-endpoint__block">
                <div className="ds-endpoint__label">Ответ</div>
                <CodeBlock code={ep.response} />
            </div>
        </div>
    </div>
);

const EndpointAccordion: React.FC = () => {
    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    return (
        <div className="ds-accordion">
            {ENDPOINT_GROUPS.map(group => (
                <div key={group.title} className="ds-accordion__group">
                    <div className="ds-accordion__group-title">{group.title}</div>
                    <Collapse
                        ghost
                        expandIconPosition="end"
                        activeKey={activeKeys}
                        onChange={keys => setActiveKeys(keys as string[])}
                        items={group.endpoints.map(ep => ({
                            key: `${group.title}:${ep.path}:${ep.method}`,
                            label: (
                                <span className="ds-accordion__label">
                                    <MethodBadge method={ep.method} />
                                    <code className="ds-accordion__path">{ep.path}</code>
                                    <span className="ds-accordion__desc">{ep.desc}</span>
                                </span>
                            ),
                            children: <EndpointPanel ep={ep} />,
                        }))}
                    />
                </div>
            ))}
            {/* Небольшая подсказка — кликните, чтобы развернуть */}
            <div className="ds-accordion__hint">
                <DownOutlined style={{ marginRight: 6 }} />
                Нажмите на эндпоинт, чтобы увидеть полный пример запроса и ответа
            </div>
        </div>
    );
};

/* ── main ──────────────────────────────────────────────────── */

function Docs() {
    const [showTop, setShowTop] = useState(false);
    const { token } = theme.useToken();

    useEffect(() => {
        const cb = () => setShowTop(window.scrollY > 400);
        window.addEventListener('scroll', cb, { passive: true });
        return () => window.removeEventListener('scroll', cb);
    }, []);

    const scrollTop = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), []);

    /* anchor link hash sync */
    const [activeAnchor, setActiveAnchor] = useState('');
    useEffect(() => {
        const cb = () => {
            const h = TOC_ITEMS.map(i => document.querySelector(i.href))
                .filter(Boolean) as HTMLElement[];
            for (let i = h.length-1; i >= 0; i--) {
                if (h[i].getBoundingClientRect().top <= 120) {
                    setActiveAnchor(TOC_ITEMS[i].href.slice(1));
                    return;
                }
            }
            setActiveAnchor('');
        };
        window.addEventListener('scroll', cb, { passive: true });
        return () => window.removeEventListener('scroll', cb);
    }, []);

    return (
        <div className="docs app-page">
            <AppHeader title="Документация — Upload API" link="/" />

            <div className="app-container docs__layout">
                {/* ── Sidebar ───────────────────────────── */}
                <aside className="ds-sidebar">
                    <nav className="ds-nav">
                        <div className="ds-nav__title">
                            <BookOutlined style={{ marginRight: 8 }} />
                            Содержание
                        </div>
                        <Anchor
                            affix={false}
                            targetOffset={token.Layout?.headerHeight || 64}
                            getCurrentAnchor={() => `#${activeAnchor}`}
                            items={TOC_ITEMS.map(i => ({
                                key:   i.key,
                                href:  i.href,
                                title: i.title,
                            }))}
                            onClick={(e) => { e.preventDefault(); document.querySelector(e.currentTarget.hash)?.scrollIntoView({ behavior:'smooth', block:'start' }); }}
                        />
                    </nav>
                </aside>

                {/* ── Content ───────────────────────────── */}
                <main className="ds-main">
                    <div className="ds-hero">
                        <ApiOutlined className="ds-hero__icon" />
                        <Title className="ds-hero__title">Upload API</Title>
                        <Paragraph className="ds-hero__lead">
                            Управление каталогом через REST API — фильмы, сериалы, сезоны, эпизоды и озвучки.
                            Все эндпоинты требуют аутентификации Bearer-токеном.
                        </Paragraph>
                        <div className="ds-hero__meta">
                            <Tag color="blue">v1</Tag>
                            <Text type="secondary">Базовый URL: </Text>
                            <code className="ds-inline-code">/api/v1/upload</code>
                        </div>
                    </div>

                    {/* 1. Auth */}
                    <Section id="auth">
                        <H2>1. Аутентификация</H2>
                        <Paragraph>
                            Доступ к Upload API — через <strong>Bearer-токен</strong>. Токен выдаётся после входа
                            с логином и паролем аккаунта администратора, контент-менеджера или студии.
                        </Paragraph>

                        <H3>Шаг 1 — Получить токен</H3>
                        <Paragraph>
                            Отправьте POST-запрос на <code>/api/v1/login-cm</code> с <code>email</code> (логин) и <code>password</code>.
                            В ответе придёт <code>access_token</code> — его и нужно использовать.
                        </Paragraph>

                        <table className="ds-table">
                            <thead><tr><th>Параметр</th><th>Тип</th><th>Обязательность</th><th>Описание</th></tr></thead>
                            <tbody>
                            <tr><td><code>email</code></td><td>string</td><td>✅</td><td>Логин пользователя</td></tr>
                            <tr><td><code>password</code></td><td>string</td><td>✅</td><td>Пароль</td></tr>
                            </tbody>
                        </table>

                        <H3>Запрос</H3>
                        <CodeBlock code={LOGIN_REQUEST} label="HTTP" />

                        <H3>Ответ — 200 OK</H3>
                        <CodeBlock code={LOGIN_RESPONSE} label="JSON" />

                        <CodeTabs samples={LOGIN_EXAMPLES} />

                        <Callout type="warning" title="Срок действия токена">
                            Токен действует <strong>2 недели</strong>. По истечении — повторите вход и получите новый.
                        </Callout>

                        <H3>Шаг 2 — Использовать токен</H3>
                        <Paragraph>
                            Передавайте токен в заголовке <code>Authorization</code> каждого запроса
                            к Upload API: <code>Authorization: Bearer {'{access_token}'}</code>.
                        </Paragraph>
                        <CodeBlock code={AUTH_HEADER_EXAMPLE} label="Shell" />

                        <table className="ds-table">
                            <thead><tr><th>Заголовок</th><th>Значение</th></tr></thead>
                            <tbody>
                            <tr><td><code>Authorization</code></td><td><code>Bearer {'{access_token}'}</code></td></tr>
                            <tr><td><code>Accept</code></td><td><code>application/json</code></td></tr>
                            <tr><td><code>Content-Type</code></td><td><code>application/json</code> (POST / PUT)</td></tr>
                            </tbody>
                        </table>
                    </Section>

                    {/* 2. Conventions */}
                    <Section id="conventions">
                        <H2>2. Общие соглашения</H2>

                        <H3>Формат ответа</H3>
                        <Paragraph>Все ответы обёрнуты в универсальный конверт:</Paragraph>
                        <CodeBlock code={`{
  "success": true,
  "data": { … },
  "message": "Сообщение",
  "status": 200
}`} />

                        <H3>Пагинация</H3>
                        <Paragraph>Параметры: <code>page</code> (≥1), <code>per_page</code> (1–100, по умолчанию 50), <code>sort_by</code>, <code>sort_order</code> (asc/desc).</Paragraph>
                        <CodeBlock code={`{
  "data": [ … ],
  "pagination": { "page": 1, "perPage": 50, "total": 123, "totalPages": 3 }
}`} />

                        <H3>Именование полей</H3>
                        <Paragraph>
                            Запросы и ответы — <strong>camelCase</strong>:{' '}
                            <code>titleOriginal</code>, <code>releaseYear</code>, <code>kinopoiskId</code>,{' '}
                            <code>imdbId</code>, <code>videoFile</code>, <code>voiceoverId</code>,{' '}
                            <code>hasInternalAds</code>, <code>exclusiveDays</code>, <code>sortOrder</code>, <code>isActive</code>.
                        </Paragraph>

                        <H3>Видеофайлы</H3>
                        <Paragraph>
                            API не принимает бинарные файлы — видео сначала загружается в FTP-хранилище,
                            а в запросе передаётся его путь (<code>videoFile</code>). После успешного
                            добавления файл перемещается в хранилище источников и попадает в очередь конвертации.
                        </Paragraph>
                        <Callout>
                            <strong>Статусы видео:</strong>{' '}
                            <Tag>draft</Tag> <Tag>active</Tag> <Tag color="processing">processing</Tag>{' '}
                            <Tag color="error">error</Tag> <Tag color="warning">deleting</Tag>{' '}
                            <Tag color="default">deleted</Tag>
                        </Callout>
                    </Section>

                    {/* 3. Endpoints */}
                    <Section id="endpoints">
                        <H2>3. Эндпоинты</H2>
                        <Paragraph>
                            Полные примеры запросов и ответов для каждого эндпоинта — разверните нужный.
                        </Paragraph>
                        <EndpointAccordion />
                    </Section>

                    {/* 4. Movies */}
                    <Section id="movies">
                        <H2>4. Фильмы</H2>

                        <H3><MethodBadge method="POST" /> Создать фильм — <code>/movies</code></H3>
                        <ParamTable rows={[
                            ['title', 'string', '✅', 'Название фильма'],
                            ['titleOriginal', 'string', '—', 'Оригинальное название'],
                            ['description', 'string', '—', 'Описание'],
                            ['releaseYear', 'integer', '—', 'Год выпуска'],
                            ['kinopoiskId', 'integer', '—', 'ID Кинопоиска'],
                            ['imdbId', 'string', '—', 'IMDB ID (tt1234567)'],
                            ['isActive', 'boolean', '—', 'Активен ли'],
                            ['categories', 'array', '—', 'ID категорий'],
                            ['tags', 'array', '—', 'ID тегов'],
                            ['trailerUrl', 'string', '—', 'URL трейлера, до 255 символов'],
                            ['voiceovers', 'array', '✅', 'Минимум 1 озвучка'],
                            ['voiceovers[].voiceoverId', 'integer', '✅', 'ID озвучки из справочника'],
                            ['voiceovers[].videoFile', 'string', '✅', 'Путь к файлу в FTP'],
                            ['voiceovers[].hasInternalAds', 'boolean', '—', 'Внутренняя реклама, по умолчанию false'],
                            ['voiceovers[].exclusiveDays', 'integer', '—', 'Эксклюзив, 0–14 дней'],
                        ]} />

                        <H4>Тело ответа — 201 Created</H4>
                        <CodeBlock code={MOVIE_RESPONSE} label="JSON" />

                        <Callout>
                            Сразу после создания фильм находится в статусе <Tag color="processing">processing</Tag>.
                            Изменять и удалять его до завершения конвертации нельзя (HTTP <code>403</code>).
                        </Callout>
                    </Section>

                    <Section id="movies-examples">
                        <H3><CodeOutlined style={{marginRight:8}}/>Примеры кода — создание фильма</H3>
                        <CodeTabs samples={MOVIE_CREATE_EXAMPLES} />

                        <H4>Переменные окружения</H4>
                        <CodeBlock code={`export API_BASE_URL="https://api.vibix.org/api/v1/upload"
export ACCESS_TOKEN="токен-из-login-cm"`} label="Shell" />
                    </Section>

                    {/* 5. Serials */}
                    <Section id="serials">
                        <H2>5. Сериалы и эпизоды</H2>

                        <H3><MethodBadge method="POST" /> Создать сериал — <code>/serials</code></H3>
                        <Paragraph>
                            Аналогичен созданию фильма (<code>title</code> обязательно), но поля в POST —
                            <strong>snake_case</strong>: <code>title_original</code>, <code>release_year</code>,
                            <code>kinopoisk_id</code>, <code>imdb_id</code>, <code>trailer_url</code> (до 1000 симв.).
                            При обновлении (PUT) — camelCase, все поля необязательны.
                        </Paragraph>

                        <H3><MethodBadge method="POST" /> Опубликовать — <code>/serials/{'serial_id'}/publish</code></H3>
                        <Paragraph>Делает сериал видимым для публикаторов. Требуется роль администратора или контент-менеджера.</Paragraph>

                        <H4>Параметры эпизода</H4>
                        <ParamTable rows={[
                            ['sortOrder', 'integer', '✅', 'Номер эпизода'],
                            ['filmName', 'string', '—', 'Название эпизода'],
                            ['description', 'string', '—', 'Описание'],
                            ['releaseYear', 'integer', '—', 'Год'],
                            ['kinopoiskId', 'integer', '—', 'ID Кинопоиска'],
                            ['imdbId', 'string', '—', 'IMDB ID'],
                            ['isActive', 'boolean', '—', 'Активен ли'],
                            ['voiceovers', 'array', '✅', 'Минимум 1 озвучка'],
                        ]} />

                        <H3><MethodBadge method="POST" /> Массовое создание — <code>.../episodes/mass</code></H3>
                        <Paragraph>Несколько эпизодов одной озвучкой за один запрос. <code>{'{number}'}</code> в filmName заменяется номером эпизода.</Paragraph>
                        <CodeBlock code={MASS_EPISODES_EXAMPLE} label="JSON" />
                    </Section>

                    {/* 6. Voiceovers */}
                    <Section id="voiceovers">
                        <H2>6. Озвучки</H2>

                        <H3><MethodBadge method="POST" /> Добавить — <code>/movies/{'movie_id'}/voiceovers</code></H3>
                        <ParamTable rows={[
                            ['voiceoverId', 'integer', '✅', 'ID озвучки из справочника'],
                            ['videoFile', 'string', '✅', 'Путь к файлу в FTP'],
                            ['hasInternalAds', 'boolean', '—', 'Внутренняя реклама'],
                            ['isActive', 'boolean', '—', 'По умолчанию true'],
                            ['exclusiveDays', 'integer', '—', 'Эксклюзив, 0–14 дней'],
                        ]} />

                        <H3><MethodBadge method="PUT" /> Обновить — <code>/movies/{'{movie_id}'}/voiceovers/{'{vo_id}'}/{'{v_id}'}</code></H3>
                        <Paragraph>Поля: <code>voiceoverId</code> (обязательно), <code>hasInternalAds</code>, <code>isActive</code>, <code>exclusiveDays</code>.</Paragraph>

                        <Callout type="warning" title="Эксклюзив">
                            Установка <code>exclusiveDays</code> требует домена студии в <code>studio_domains</code>.
                            Иначе — ошибка <code>422</code>. Диапазон: 0–14 дней.
                        </Callout>
                        <Callout type="warning" title="Последняя озвучка">
                            Удалить последнюю озвучку фильма или эпизода нельзя (<code>422</code>).
                        </Callout>
                    </Section>

                    {/* 7. Reference */}
                    <Section id="reference">
                        <H2>7. Справочники</H2>
                        <table className="ds-table">
                            <thead><tr><th>Эндпоинт</th><th>Описание</th></tr></thead>
                            <tbody>
                            <tr><td><MethodBadge method="GET" /> <code>/reference/voiceovers</code></td><td>Активные озвучки (ID для voiceoverId), поиск по <code>?search=</code></td></tr>
                            <tr><td><MethodBadge method="GET" /> <code>/reference/categories</code></td><td>Категории, жанры, страны (кэш 24 ч)</td></tr>
                            <tr><td><MethodBadge method="GET" /> <code>/reference/info</code></td><td>Метаданные: <code>?imdb_id=tt0816692</code> или <code>?kp_id=462682</code></td></tr>
                            <tr><td><MethodBadge method="GET" /> <code>/videos/{'video_id'}/status</code></td><td>Текущий статус конкретного видео</td></tr>
                            </tbody>
                        </table>
                    </Section>

                    {/* 8. Errors */}
                    <Section id="errors">
                        <H2>8. Коды ошибок</H2>
                        <StatusTable rows={[
                            ['200', 'Успех', 'GET / PUT / DELETE'],
                            ['201', 'Создано', 'POST'],
                            ['400', 'Некорректный запрос', '—'],
                            ['401', 'Токен отсутствует / недействителен', '—'],
                            ['403', 'Нет прав / видео в обработке / аккаунт неактивен', '«Фильм в процессе обработки»'],
                            ['404', 'Ресурс или файл не найден', '«Файл не найден в FTP»'],
                            ['409', 'Конфликт: дубликат файла', '«Файл … уже добавлен: сериал/фильм … (видео #id)» + data.conflict'],
                            ['422', 'Ошибка валидации или бизнес-логики', '«Нельзя удалить последнюю озвучку»'],
                            ['429', 'Слишком много запросов', '—'],
                            ['500', 'Внутренняя ошибка сервера', '«An error occurred»'],
                            ['503', 'Upload API отключён', '«Upload API is temporarily disabled.»'],
                        ]} />
                        <H3>Формат ошибки</H3>
                        <CodeBlock code={ERROR_BODY} label="JSON" />
                    </Section>

                    {/* 9. Full examples */}
                    <Section id="examples">
                        <H2>9. Примеры кода</H2>
                        <Paragraph>Ниже — полные рабочие примеры для каждого языка. Подставьте свои переменные окружения и запускайте.</Paragraph>
                        {MOVIE_CREATE_EXAMPLES.map(s => (
                            <CodeBlock key={s.lang} code={s.code} label={s.label} />
                        ))}
                    </Section>

                    {/* 10. Tips */}
                    <Section id="tips">
                        <H2>10. Рекомендации</H2>
                        <ul className="ds-tips">
                            <li>Перед созданием проверяйте доступные озвучки через{' '}
                                <MethodBadge method="GET" /> <code>/reference/voiceovers</code> — ID гарантированно валидны.</li>
                            <li>Следите за статусом: <code>GET /videos/{'{video_id}'}/status</code>.
                                Конвертация не мгновенна — не пытайтесь изменить видео в процессе.</li>
                            <li>Для больших сериалов используйте <MethodBadge method="POST" /> массовое создание эпизодов.</li>
                            <li>Повторная загрузка того же файла → <code>409 Conflict</code> — система проверяет MD5.</li>
                            <li>Без <code>kinopoiskId</code>/<code>imdbId</code> генерируется внутренний ID — можно использовать для черновиков.</li>
                        </ul>
                    </Section>

                    {/* spacer so last section is visible above sidebar */}
                    <div style={{ height: 60 }} />
                </main>
            </div>

            {/* Back to top FAB */}
            {showTop && (
                <button className="ds-fab" onClick={scrollTop} aria-label="Наверх">
                    <ArrowUpOutlined />
                </button>
            )}
        </div>
    );
}

export default Docs;
